import React from 'react';
import Model from 'react-body-highlighter';
import { useProfile } from '../context/ProfileContext';

/**
 * AnatomyDiagram — properly proportioned front+back muscle diagram that
 * replaces the hand-coded SVG paths we shipped earlier. Wraps
 * `react-body-highlighter` (MIT, polygon-accurate) and maps Enferno's
 * internal muscle taxonomy onto the library's named muscle groups.
 *
 * Highlighting tiers (mode-aware — Phase 4):
 *   - Athlete: primary ember red (#ef4444), secondary deep brick (#7f1d1d),
 *     body zinc-800 (#27272a)
 *   - Adaptive: primary muted teal (#6B9A93), secondary lavender (#ccb1f1),
 *     body soft sand-tan (#D9CFB8)
 *   - Unworked muscles in either mode use body color
 *
 * Note on glow / breathing: two attempts have been made to add an ember
 * drop-shadow glow + breathing pulse to highlighted muscles (SMIL
 * approach, then a CSS filter approach). Neither rendered visibly in
 * browser despite syntactically-correct CSS. `react-body-highlighter` uses
 * inline `<polygon style="fill:...">` rendering, which makes CSS override
 * behavior finicky. Backlogged for a focused investigation session — see
 * MASTER_BACKLOG.md "Anatomy diagram muscle breathing".
 *
 * Props:
 *   primary    string[]  Enferno internal muscle names (e.g. ['chest','triceps'])
 *   secondary  string[]  same
 *   size       number    pixel width per side (default 240) — height auto-scales
 *   region     'full' | 'upper' | 'lower' | 'core'   crop window (default 'full')
 *              Used by Library card thumbnails to show only the relevant body region
 *              so chest exercises don't show legs etc. Use the exported
 *              regionOf(primaryMuscles) helper to derive this automatically.
 *   showLabels boolean   whether to render the small "Front" / "Back" labels (default true)
 */

// Athlete palette (default)
const PRIMARY_COLOR_ATHLETE   = '#ef4444';   // ember red — matches --enf-anatomy-primary (root)
const SECONDARY_COLOR_ATHLETE = '#7f1d1d';   // deep brick — matches --enf-anatomy-secondary (root)
const BODY_COLOR_ATHLETE      = '#27272a';   // zinc-800

// Adaptive palette — blue family on sand-tan body (Phase 4c walkthrough)
const PRIMARY_COLOR_ADAPTIVE   = '#1E4D7B';  // deep blue — primary mover (replaces muted teal)
const SECONDARY_COLOR_ADAPTIVE = '#5A8FB8';  // mid blue — secondary engagement (replaces lavender)
const BODY_COLOR_ADAPTIVE      = '#D9CFB8';  // sand-tan

// Enferno internal name → react-body-highlighter muscle key
// (multiple internal names can map to the same library key when our
// taxonomy is finer than theirs, e.g. upper-chest → chest)
//
// Honest limits: react-body-highlighter has no polygon for ankles, hip flexors,
// breath/diaphragm, palms, wrists, tibialis, or spine. We map those to their
// closest visual approximation (calves for ankles, abs for hip flexors / breath,
// forearm for palms / wrists, etc.). Adductors highlight only on the POSTERIOR
// view because the library has no anterior adductor polygon — front view shows
// the upper body honestly without inventing a polygon that isn't there.
const MUSCLE_MAP = {
  'chest':        ['chest'],
  'upper-chest':  ['chest'],
  'back':         ['upper-back', 'lower-back'],
  'lats':         ['upper-back'],
  'upper-back':   ['upper-back'],
  'lower-back':   ['lower-back'],
  'spine':        ['upper-back', 'lower-back'],          // no spine polygon — back is visual proxy
  'traps':        ['trapezius'],
  'lower-traps':  ['trapezius'],
  'shoulders':    ['front-deltoids', 'back-deltoids'],
  'front-delts':  ['front-deltoids'],
  'rear-delts':   ['back-deltoids'],
  'rotator-cuff': ['back-deltoids'],                     // closest visual approximation
  'neck':         ['neck'],
  'biceps':       ['biceps'],
  'triceps':      ['triceps'],
  'forearms':     ['forearm'],
  'forearm':      ['forearm'],
  'palms':        ['forearm'],                           // no hand polygon — forearm distal end
  'wrists':       ['forearm'],                           // no wrist polygon — forearm distal end
  'core':         ['abs'],
  'abs':          ['abs'],
  'obliques':     ['obliques'],
  'serratus':     ['obliques'],                          // serratus sits adjacent to obliques
  'hip-flexors':  ['abs'],                               // no polygon — lower abdomen is visual proxy
  'breath':       ['abs'],                               // diaphragm sits behind ribcage — abs is closest cue
  'quads':        ['quadriceps'],
  'quadriceps':   ['quadriceps'],
  'hamstrings':   ['hamstring'],
  'hamstring':    ['hamstring'],
  'legs':         ['quadriceps', 'hamstring'],           // generic — both upper-leg polygons
  'glutes':       ['gluteal'],
  'gluteal':      ['gluteal'],
  'calves':       ['calves'],
  'tibialis':     ['calves'],                            // no anterior shin polygon — calf area is closest
  'ankles':       ['calves'],                            // no ankle polygon — lower leg is visual proxy
  'adductors':    ['adductor'],                          // posterior view only — library has no anterior polygon
  'abductors':    ['abductors'],
  // Compound/conditioning moves tagged primaryMuscles:['full-body'] (Burpee, Bear
  // Crawl, Mountain Climbers, Kettlebell Swing, etc.) had NO entry here, so they
  // rendered with zero highlighted muscles despite `regionOf` correctly showing the
  // whole body — a genuinely blank diagram. Light up a representative spread across
  // upper/core/lower so a full-body move visually reads as "hits everything."
  'full-body':    ['chest', 'upper-back', 'abs', 'quadriceps', 'hamstring'],
};

// Body-region classification for cropping. Used by the Library grid cards so
// chest exercises don't show legs etc. Core/full body lifts fall back to 'full'.
const UPPER_MUSCLES = new Set([
  'chest', 'upper-chest', 'back', 'lats', 'upper-back', 'lower-back', 'spine', 'traps', 'lower-traps',
  'shoulders', 'front-delts', 'rear-delts', 'rotator-cuff',
  'biceps', 'triceps', 'forearms', 'forearm', 'palms', 'wrists', 'neck', 'serratus',
]);
const LOWER_MUSCLES = new Set([
  'quads', 'quadriceps', 'hamstrings', 'hamstring', 'legs', 'glutes', 'gluteal',
  'calves', 'tibialis', 'ankles', 'adductors', 'abductors',
]);
const CORE_MUSCLES = new Set(['core', 'abs', 'obliques', 'hip-flexors', 'breath']);

export const regionOf = (primaryMuscles = []) => {
  if (!primaryMuscles || primaryMuscles.length === 0) return 'full';
  if (primaryMuscles.includes('full-body')) return 'full';
  const hasUpper = primaryMuscles.some((m) => UPPER_MUSCLES.has(m));
  const hasLower = primaryMuscles.some((m) => LOWER_MUSCLES.has(m));
  const hasCore = primaryMuscles.some((m) => CORE_MUSCLES.has(m));
  if (hasUpper && hasLower) return 'full'; // conflict → show whole body
  if (hasUpper) return 'upper';
  if (hasLower) return 'lower';
  if (hasCore) return 'core';
  return 'full';
};

// Crop windows. Body in react-body-highlighter renders at roughly 0.44 aspect
// (width/height) — so at width=130, natural height ≈ 295px. heightFactor is
// the fraction of natural body height that should remain visible; translateYPct
// shifts the body inside the crop window so the right region lands in frame.
const REGION_CROPS = {
  full:  { heightFactor: 1.0,  translateYPct: 0 },
  upper: { heightFactor: 0.5,  translateYPct: 0 },     // top half — head/torso/arms
  core:  { heightFactor: 0.32, translateYPct: -32 },   // mid 32% — torso/abs
  lower: { heightFactor: 0.55, translateYPct: -45 },   // bottom 55% — pelvis/legs/feet
};
// Approximate natural aspect ratio of the body SVG (width / height).
// Used to compute the crop window height in pixels from `size` (width).
const BODY_NATURAL_ASPECT = 0.44;

const mapMuscles = (list = []) => {
  const out = new Set();
  list.forEach((m) => {
    const mapped = MUSCLE_MAP[m] || [];
    mapped.forEach((x) => out.add(x));
  });
  return Array.from(out);
};

const AnatomyDiagram = ({
  primary = [],
  secondary = [],
  size = 240,
  region = 'full',
  showLabels = true,
}) => {
  const { profile } = useProfile();
  const adaptive = !!profile?.adaptiveMode;
  const PRIMARY_COLOR   = adaptive ? PRIMARY_COLOR_ADAPTIVE   : PRIMARY_COLOR_ATHLETE;
  const SECONDARY_COLOR = adaptive ? SECONDARY_COLOR_ADAPTIVE : SECONDARY_COLOR_ATHLETE;
  const BODY_COLOR      = adaptive ? BODY_COLOR_ADAPTIVE      : BODY_COLOR_ATHLETE;
  // Combine primary + secondary into the IExerciseData[] shape the library
  // expects. We use the "frequency" trick — secondary muscles appear once,
  // primary muscles appear twice, so highlightedColors[0] = secondary tint,
  // highlightedColors[1] = primary tint.
  const data = React.useMemo(() => {
    const primaryMapped = mapMuscles(primary);
    const secondaryMapped = mapMuscles(secondary);
    const secondaryEntry = secondaryMapped.length
      ? [{ name: 'Secondary engagement', muscles: secondaryMapped }]
      : [];
    const primaryEntry = primaryMapped.length
      ? [
          { name: 'Primary mover', muscles: primaryMapped },
          { name: 'Primary mover', muscles: primaryMapped },
        ]
      : [];
    return [...secondaryEntry, ...primaryEntry];
  }, [primary, secondary]);

  const containerStyle = { width: `${size}px`, padding: 0 };
  const svgStyle = { width: '100%', height: 'auto', display: 'block' };

  const crop = REGION_CROPS[region] || REGION_CROPS.full;
  const naturalHeight = size / BODY_NATURAL_ASPECT;
  const cropHeight = Math.round(naturalHeight * crop.heightFactor);

  const renderModel = (type) => {
    const model = (
      <Model
        data={data}
        type={type}
        bodyColor={BODY_COLOR}
        highlightedColors={[SECONDARY_COLOR, PRIMARY_COLOR]}
        style={containerStyle}
        svgStyle={svgStyle}
      />
    );
    if (region === 'full') return model;
    return (
      <div style={{ width: size, height: cropHeight, overflow: 'hidden', position: 'relative' }}>
        <div style={{ transform: `translateY(${crop.translateYPct}%)` }}>
          {model}
        </div>
      </div>
    );
  };

  return (
    <div className="enferno-anatomy flex flex-col sm:flex-row items-center justify-center gap-4">
      <div className="flex flex-col items-center">
        {renderModel('anterior')}
        {showLabels && <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mt-1">Front</div>}
      </div>
      <div className="flex flex-col items-center">
        {renderModel('posterior')}
        {showLabels && <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mt-1">Back</div>}
      </div>
    </div>
  );
};

export default AnatomyDiagram;
